import { splitCsvOrArray } from "./util/queryParams.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/footprint.ts
const PREFERENCE_SUB_TYPES = [
	"language",
	"username",
	"prompt_helper",
	"tsubaki_prompt_helper",
	"tsubaki2_prompt_helper"
];
const ACTION_SUB_TYPES = [
	"artwork_like",
	"artwork_dislike",
	"artwork_publish"
];
const MemSource = [
	"api_sync",
	"cron_job",
	"agent"
];
const ACTION_LIST_DEFAULT_LIMIT = 50;
const ACTION_LIST_MAX_LIMIT = 100;
const ACTION_LIST_MAX_SUB_TYPES = 10;
const preferenceEntrySchema = z.object({
	key: z.enum(PREFERENCE_SUB_TYPES).meta({ description: "Preference sub-type, e.g. \"language\" or \"username\"" }),
	text: z.string().meta({ description: "Natural-language description stored for the preference" }),
	lastUpdatedAt: z.iso.datetime().meta({ description: "ISO 8601 timestamp of the last update" })
});
const preferenceSubTypeEnum = z.enum(PREFERENCE_SUB_TYPES);
const listPreferencesInputSchema = z.object({
	userId: z.coerce.bigint(),
	keys: z.string().optional().transform((s) => s?.split(",").map((x) => x.trim()).filter(Boolean)).pipe(z.array(preferenceSubTypeEnum).nonempty().optional())
});
const listPreferencesOutputSchema = z.array(preferenceEntrySchema);
const actionSubTypeEnum = z.enum(ACTION_SUB_TYPES);
const actionEntrySchema = z.object({
	id: z.string().meta({ description: "Stable turbopuffer row id, e.g. `artwork_like-12345`. Useful for client-side dedup and debugging." }),
	subType: actionSubTypeEnum.meta({ description: "Action sub-type, e.g. `artwork_like` or `artwork_publish`" }),
	text: z.string().meta({ description: "Natural-language description stored for the action" }),
	refId: z.string().optional().meta({ description: "Target entity id (e.g. artwork id) the action references, when one applies." }),
	source: z.enum(MemSource).optional().meta({ description: "Ingestion source (api_sync / cron_job / agent). Present only when the requester is an admin." }),
	lastUpdatedAt: z.iso.datetime().meta({ description: "ISO 8601 timestamp of the most recent upsert" })
});
const listActionsInputSchema = z.object({
	userId: z.coerce.bigint(),
	subTypes: z.union([z.string(), z.array(z.string())]).optional().transform((value) => value === void 0 ? void 0 : splitCsvOrArray(value)).pipe(z.array(actionSubTypeEnum).nonempty().max(10).optional()),
	limit: z.coerce.number().int().min(1).max(100).optional(),
	cursor: z.string().optional()
});
const listActionsOutputSchema = z.object({
	entries: z.array(actionEntrySchema),
	nextCursor: z.string().optional()
});
const footprintContract = oc.prefix("/footprint").tag("Footprint").router({
	listPreferences: oc.route({
		method: "GET",
		path: "/{userId}/preference",
		summary: "List footprint preferences for a user",
		description: "Returns the top 50 most-recently-updated preference entries from the user's footprint namespace, sorted by `lastUpdatedAt` descending. The set of preference sub-types is bounded by design, so 50 is well beyond what any user can accumulate and no pagination is exposed. Pass `?keys=a,b` to restrict the response to a subset of sub-types. Accessible only to the user themselves or to admins. Responds 404 when the user's namespace has never been written to, and `[]` when the namespace exists but contains no preference entries."
	}).input(listPreferencesInputSchema).output(listPreferencesOutputSchema).errors({
		UNAUTHORIZED: {
			status: 401,
			message: "User not authenticated"
		},
		FORBIDDEN: {
			status: 403,
			message: "Access denied"
		},
		NOT_FOUND: {
			status: 404,
			message: "Footprint namespace not found for user"
		}
	}),
	listActions: oc.route({
		method: "GET",
		path: "/{userId}/action",
		summary: "List footprint actions for a user",
		description: `Returns the user's recorded action entries (likes, dislikes, publishes) sorted by \`lastUpdatedAt\` descending. Pass \`?subTypes=a,b\` to restrict to a subset, \`?limit=N\` (1–100, default 50) to control page size, and \`?cursor=...\` to fetch the next page using the \`nextCursor\` returned by the previous call. The \`source\` field (api_sync / cron_job / agent) is included only for admin requesters. Note: rows whose \`lastUpdatedAt\` is exactly equal at the cursor boundary may be skipped — millisecond collisions are rare in practice. Accessible only to the user themselves or to admins. Responds 404 when the namespace has never been written to, and an empty \`entries\` array when it exists but contains no matching action entries.`
	}).input(listActionsInputSchema).output(listActionsOutputSchema).errors({
		UNAUTHORIZED: {
			status: 401,
			message: "User not authenticated"
		},
		FORBIDDEN: {
			status: 403,
			message: "Access denied"
		},
		NOT_FOUND: {
			status: 404,
			message: "Footprint namespace not found for user"
		}
	})
});
//#endregion
export { ACTION_LIST_DEFAULT_LIMIT, ACTION_LIST_MAX_LIMIT, ACTION_LIST_MAX_SUB_TYPES, footprintContract, listActionsInputSchema };
